// ComparisonView.jsx - Cleaned up to remove unnecessary paragraph duplication
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import PagerWrapper from './PagerWrapper';
import ParagraphRenderer from './ParagraphRenderer';
import ParagraphContainer from './ParagraphContainer';
import ShareToCompare from './ShareToCompare';
import useSelectionData from 'hooks/useSelectionData';
import UserDropdownHeader from './UserDropdownHeader';
import { useTranscriptData } from 'hooks/useTranscriptData';
import { TRANSCRIPT_NAVIGATION_PADDING } from 'constants/DimensionsConstants';
import PageLoadingComponent from './PageLoadingComponent';
import CodeableContext from 'contexts/CodeableContext';
import EditableSideWrapper from './EditableSide';
import { useCurrentUser } from '../../hooks/users/queries';
import ColorConstants from 'constants/ColorConstants';
import ComparisonUrlUtil from 'utils/ComparisonUrlUtil';
import ComparisonHeatmapStrip, { CELL_COLORS } from './ComparisonHeatmapStrip';
import { useActiveCode } from 'contexts/ActiveCodeContext';
import { useCodes } from '../../hooks/codes/queries';
import { getUserName } from './UserDropdown';
import {
  buildHoverUnderlineSpans,
  buildOverlayEntries,
  createOverlayCache,
  filterCodingsToUser,
  selectionCutsForParagraph,
} from './codeDiffUtils';

// UserDropdown's display-name convention; the fallback covers a coder the
// users list hasn't resolved yet.
const displayName = (user) => getUserName(user) || 'This coder';

// Stable shared empty array: a fresh [] every render would
// invalidate the whole memo chain below (diffs, tooltip segments, CSS) and
// churn the document-level hover listeners with it.
const EMPTY_ARRAY = [];

const PADDING = `${TRANSCRIPT_NAVIGATION_PADDING}px`;

// Tints of the agreement colors (see ComparisonHeatmapStrip for the
// validated palette), behind a solid border so recolored chips still read as
// "a Delve code chip" rather than a flat colored badge. These are the
// deepened tints — per Alex, chips wear them permanently, in every state
// (the earlier pale-at-rest/darker-on-hover split read as white chips at
// rest); text stays readable on both.
// There is no `partial` tint: a partly-matching code isn't a third state, it's
// "right about some of the text, wrong about the rest," so partial chips are
// rendered half agree-green / half conflict-red (see splitChipBackground)
// instead of a third color.
const CHIP_FILLS = {
  agree: '#a9dcc9',
  conflict: '#efc0b9',
  // Neutral gray for oneSided chips (only one coder applied this code here
  // at all — not compared, so it shouldn't wear the code's own color as if
  // it were a normal match; per Alex AND Matthew independently). Border
  // comes from CELL_COLORS.oneSided (already the heatmap's one-sided gray);
  // this is just its chip tint.
  oneSided: ColorConstants.GRAY_200,
};

// Split treatment for partial chips: agree-green top-left half, conflict-red
// bottom-right half — a hard 50/50 split, not a gradient, so both halves stay
// the pure validated hues (a green→red blend passes through a muddy brown
// that just re-invents the amber this replaces, and reads as if agreement
// varied across the chip). The seam is DIAGONAL (fixed 45° through the
// center, not corner-to-corner, so its angle doesn't change with chip width):
// on this side-by-side page a vertical split would map onto "left coder /
// right coder" and misread as a verdict on each coder, and the diagonal is
// the familiar dual-state idiom (split color swatches). Two background layers
// rebuild the chip's anatomy per half: the top layer is the tint fills
// clipped to padding-box, the bottom
// layer is the saturated agreement hues clipped to border-box so, with
// border-color transparent, it shows through only in the 1px border ring — a
// split border that keeps the chip's rounded corners (border-image would
// square them off). The halves meet at a bare hard stop — no white seam
// between them; the antialiased edge plus the split border ring is enough to
// read as deliberate, and a 1px line was one more mark on an already small
// chip.
const splitChipBackground = (fills) =>
  `linear-gradient(135deg, ${fills.agree} 0 50%, ${fills.conflict} 50%) padding-box, ` +
  `linear-gradient(135deg, ${CELL_COLORS.agree} 50%, ${CELL_COLORS.conflict} 50%) border-box`;

// Underline recolor palette: the agreement colors straight from the heatmap
// cells, except one-sided spans (coded by only one coder — neutral
// coverage info, not a disagreement) step up from the cells' faint gray to
// the app's mid gray: a 2px line needs more contrast against white than a
// 6px block needs against the strip's track, and GRAY_400 stays clearly
// quieter than the conflict red. (The partial entry — the strip's lighter
// red — serves only the tooltip dot; underlineRule special-cases partial to
// the green/red stacked pair.)
const UNDERLINE_COLORS = {
  ...CELL_COLORS,
  oneSided: ColorConstants.GRAY_400,
};

// Very-faint washes of the same agreement hues, painted behind each
// classified span so agreement reads as a block of color rather than only a
// 2px bottom edge. Kept low-alpha (~12%) rather than a solid pale hex so the
// wash blends with the native selection/hover highlight — that highlight
// paints the parent .highlightSpan's opaque background, and the wash sits
// on the child span above it, so the two tint each other instead of one
// flatly hiding the other. One-sided spans deliberately get none: coverage
// info, not a verdict, so they recede (same reasoning as their gray
// underline). Partial shares the conflict red wash — its double underline,
// not a third wash color, is what distinguishes it (see underlineRule).
const UNDERLINE_WASH_ALPHA = '1f'; // ~12%
const UNDERLINE_WASHES = {
  agree: `${CELL_COLORS.agree}${UNDERLINE_WASH_ALPHA}`,
  partial: `${CELL_COLORS.conflict}${UNDERLINE_WASH_ALPHA}`,
  conflict: `${CELL_COLORS.conflict}${UNDERLINE_WASH_ALPHA}`,
};

// The agreement skin is a SMALL, STATIC stylesheet keyed on data attributes
// that a layout effect stamps onto the rendered spans and chips (see the
// stamping effect in ComparisonView). This replaced per-span/per-chip
// generated rules keyed on `.codeSpan[start="N"]`: profiled on a 400-
// paragraph page, that sheet grew to ~4,000 attribute-selector rules
// (~540KB) whose selector matching cost ~775ms of style recalc on EVERY
// enable/disable/change — every chip hover flip and every coding edit. A
// constant sheet never re-parses, and stamping invalidates only the touched
// elements.
//
// Span rules: the wash needs no !important — .codeSpan's inline style only
// ever sets box-shadow (SubElementComponent), so background is uncontested;
// the box-shadow does, to beat that inline underline.
//
// Partly-agreeing isn't a third state, it's agreement AND disagreement on
// the same text — so partial spans wear a stacked agree-green over
// conflict-red pair, in the app's own multi-code underline pattern (2px
// line, 1px white gap, next line — see getUnderlineStyle in
// SubElementComponent) so it reads as native Delve, not a new mark. The
// wash behind the span is the conflict red (amber is dropped from the page
// entirely, per Alex): red says "something's off here" and the double
// underline explains that it's partial, not full, disagreement.
//
// Chip rules: one fill for every state — rest, hover, and selected all wear
// the same deepened tint (per Alex), the !important keeping it through the
// native chip's own inline styling. The detached X is a chip too and wears
// the SAME verdict border and fill: its native inline style is the CODE's
// own palette (CodeX.jsx), and excluding it from the background rule left
// a green agree chip wearing a pink X and a red conflict chip wearing a
// blue X — coding colors on a page that speaks only agreement colors. The
// X can't wear the partial split (it's its own tiny chip), so on partial
// it takes the conflict red border and fill — it hangs off the chip's red
// half.
const spanRule = (classification) => {
  const wash = UNDERLINE_WASHES[classification];
  const shadow = classification === 'partial'
    ? `0 2px 0 0px ${CELL_COLORS.agree}, 0 3px 0 0 white, 0 5px 0 0px ${CELL_COLORS.conflict}, 0 6px 0 0 white`
    : `0 2px 0 0px ${UNDERLINE_COLORS[classification]}, 0 3px 0 0 white`;
  return `.codeSpan[data-agreement="${classification}"] { box-shadow: ${shadow} !important;${wash ? ` background-color: ${wash};` : ''} }`;
};

// The native selected/hover excerpt highlight (the yellow .highlightSpan
// treatment from SentenceComponent) is deliberately left ALONE here: it is
// the app-wide "this snippet is selected" affordance, and per Matthew it
// stays yellow on the comparison page too — the no-amber rule from
// 3906ac8f4 covers the overlay's OWN marks (span washes, heatmap cells,
// chips), not the selection state.
const buildStaticAgreementCss = () => [
  ...['agree', 'partial', 'conflict', 'oneSided'].map(spanRule),
  ...['agree', 'conflict', 'oneSided'].map((verdict) =>
    `[data-verdict="${verdict}"] .ui-chip { border-color: ${CELL_COLORS[verdict]} !important; background-color: ${CHIP_FILLS[verdict]} !important; }`
  ),
  `[data-verdict="partial"] .code-x.ui-chip { border-color: ${CELL_COLORS.conflict} !important; background-color: ${CHIP_FILLS.conflict} !important; }`,
  `[data-verdict="partial"] .ui-chip:not(.code-x) { border-color: transparent !important; background: ${splitChipBackground(CHIP_FILLS)} !important; }`,
].join('\n');

// Exported for the agreement-skin regression test (no-amber contract).
export const AGREEMENT_STATIC_CSS = buildStaticAgreementCss();

// Builds the hover tooltip's content for one segment, strictly about THIS
// text. Agree is pure confirmation: just the "Both applied" list (identical
// sets is what agree means). Partly-agree leads with "Both applied" — codes
// both coders put on this exact span (sharedCodeIds) — then "Only X" rows
// for each coder's extras here. Disagree lists each coder's codes for this
// span under their plain names (its lists are disjoint by definition, so
// there is never a Both-applied row to contrast an "Only" prefix against).
// oneSided names the lone coder, since a bare gray underline says nothing
// about who.
const VERDICT_VERBS = { agree: 'agree', partial: 'partly agree', conflict: 'disagree' };

const tooltipContentFor = (segment, codeNamesById, leftName, rightName) => {
  const namesFor = (codeIds) => codeIds.map((id) => codeNamesById[id] || 'Unnamed code');
  if (segment.classification === 'oneSided') {
    const leftIsCoder = segment.leftCodeIds.length > 0;
    const [coderName, otherName] = leftIsCoder ? [leftName, rightName] : [rightName, leftName];
    return {
      verdict: `Only ${coderName} coded this`,
      rows: [
        { label: coderName, codes: namesFor(leftIsCoder ? segment.leftCodeIds : segment.rightCodeIds) },
        { label: otherName, codes: null },
      ],
    };
  }
  const shared = segment.sharedCodeIds || [];
  const leftOwn = segment.leftCodeIds.filter((id) => !shared.includes(id));
  const rightOwn = segment.rightCodeIds.filter((id) => !shared.includes(id));
  const ownLabel = (name) => (shared.length ? `Only ${name}` : name);
  return {
    verdict: `${leftName} and ${rightName} ${VERDICT_VERBS[segment.classification]}`,
    rows: [
      ...(shared.length ? [{ label: 'Both applied', codes: namesFor(shared) }] : []),
      ...(leftOwn.length ? [{ label: ownLabel(leftName), codes: namesFor(leftOwn) }] : []),
      ...(rightOwn.length ? [{ label: ownLabel(rightName), codes: namesFor(rightOwn) }] : []),
    ],
  };
};

const TOOLTIP_WIDTH = 300;
// Rough ceiling on the card's rendered height, used only to decide whether
// to flip above the anchor near the viewport bottom — overestimating just
// flips a little early, which is harmless.
const TOOLTIP_MAX_HEIGHT = 230;
// Cold-hover delay before the card first appears — long enough that
// crossing the text doesn't strobe cards, short of the ~500ms native-title
// sluggishness the proto rejected.
const TOOLTIP_SHOW_DELAY_MS = 350;

// The hover card itself: same white-card popover look as the app's own
// code-selector popper (StyledPopper.jsx) — border + soft shadow rather
// than a generic dark browser-style tooltip. The verdict gets true heading
// weight and a colored dot matching the span's underline color — the one
// thing worth reading at a glance — set off from the coders' code lists by
// a hairline divider, the same "header + detail rows" shape as the app's
// other popovers. pointerEvents: none so the card never steals the hover
// that summoned it.
const ComparisonAgreementTooltip = ({ tooltip, content }) => {
  const positionStyle =
    tooltip.bottom + 8 + TOOLTIP_MAX_HEIGHT > window.innerHeight
      ? { bottom: `${window.innerHeight - tooltip.top + 8}px` }
      : { top: `${tooltip.bottom + 8}px` };
  return (
    <div
      data-testid="agreement-tooltip"
      data-agreement={tooltip.segment.classification}
      style={{
        position: 'fixed',
        ...positionStyle,
        left: `${Math.max(8, Math.min(tooltip.left, window.innerWidth - TOOLTIP_WIDTH - 40))}px`,
        width: `${TOOLTIP_WIDTH}px`,
        backgroundColor: '#fff',
        border: '1px solid #e1e4e8',
        color: '#24292e',
        fontSize: '13px',
        borderRadius: '6px',
        boxShadow: '0 8px 24px rgba(149, 157, 165, 0.2)',
        zIndex: 2000,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '11px 12px',
          fontWeight: 700,
          fontSize: '14px',
          lineHeight: '1.3',
          borderBottom: '1px solid #eef0f2',
        }}
      >
        <span
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            flexShrink: 0,
            // Partial's dot is the strip's lighter red — matches its heatmap
            // cell, and the card's header text carries the meaning anyway.
            backgroundColor: UNDERLINE_COLORS[tooltip.segment.classification],
          }}
        />

        {content.verdict}
      </div>
      <div style={{ padding: '10px 12px 11px' }}>
        {content.rows.map((row, i) => (
          <div key={row.label} style={{ marginTop: i === 0 ? 0 : '10px' }}>
            <div style={{ color: '#767676', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: '1.3' }}>
              {row.label}
            </div>
            <div
              style={{
                marginTop: '3px',
                fontSize: '13px',
                lineHeight: '1.45',
                color: row.codes ? '#24292e' : '#767676',
                fontStyle: row.codes ? 'normal' : 'italic',
              }}
            >
              {row.codes ? row.codes.join(', ') : "didn't code this"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Renders one column of the side-by-side comparison as grid cells.
 * Each paragraph is wrapped in a div with explicit gridRow so CSS grid
 * aligns paragraphs across both columns — the taller paragraph determines
 * the row height and the other side stretches to match.
 * Reads selection from its own per-side CodeableContext.
 */
const ComparisonSideGrid = ({
  visibleParagraphs,
  transcriptID,
  hasEditPermission,
  showCodeSelector,
  marginLeft,
  marginRight,
  filterUserID,
  gridColumn,
  setParagraphRef,
  extraBoundariesByParagraph,
  onSelectionChange,
}) => {
  const { select, selectedExcerptId } = useContext(CodeableContext) || {};

  // Always call the hook (React rules), but ignore the result when this side
  // doesn't have edit permission. Without an EditableSideWrapper, the context
  // reads from the outer SNavigation which may have stale selection state.
  const rawSelectionData = useSelectionData(selectedExcerptId, select, transcriptID, 'Transcript');
  const selectionData = hasEditPermission ? rawSelectionData : null;

  // Report this column's active selection upward: while a selection is live,
  // the renderer re-cuts spans at its boundaries (SentenceClass
  // _createSelects), and the agreement overlay must account for the re-cut
  // spans too or they'd drop their styling (see buildUnderlineSpans'
  // extraCuts). A LAYOUT effect: the setState it triggers re-renders and
  // re-stamps synchronously before paint, so the frame that shows the
  // re-cut spans never paints with stale data-agreement attributes.
  useLayoutEffect(() => {
    onSelectionChange?.(gridColumn, selectionData);
  }, [onSelectionChange, gridColumn, selectionData]);

  return visibleParagraphs.map((paragraph, index) => {
    if (!paragraph || paragraph.start === paragraph.end) return null;

    const paragraphSelection = selectionData &&
      !(paragraph.start >= selectionData.selectionEnd || paragraph.end <= selectionData.selectionStart)
        ? selectionData : null;

    return (
      <div
        key={`${transcriptID}_${paragraph.paragraphIndex}_${gridColumn}`}
        ref={setParagraphRef ? (el) => setParagraphRef(index, el) : undefined}
        data-paragraph-index={paragraph.paragraphIndex}
        data-grid-column={gridColumn}
        style={{ gridRow: index + 1, gridColumn }}
      >
        <ParagraphContainer
          paragraph={paragraph}
          transcriptID={transcriptID}
          codeableType="Transcript"
          selection={paragraphSelection}
          absoluteIndex={paragraph.paragraphIndex}
          paragraphIndex={index}
          canEdit={false}
          isPrint={true}
          enableHover
          hasEditPermission={hasEditPermission}
          marginLeft={marginLeft}
          marginRight={marginRight}
          filterUserID={filterUserID}
          showCodeSelector={showCodeSelector}
          extraBoundaries={extraBoundariesByParagraph ? extraBoundariesByParagraph[index] : null}
        />
      </div>
    );
  }).filter(Boolean);
};

/**
 * Renders a single side's ParagraphRenderer for solo mode.
 * Reads selection from its own per-side CodeableContext.
 */
const ComparisonSide = ({
  visibleParagraphs,
  transcriptID,
  hasEditPermission,
  showCodeSelector,
  marginLeft,
  marginRight,
  filterUserID,
}) => {
  const { select, selectedExcerptId } = useContext(CodeableContext) || {};

  return (
    <ParagraphRenderer
      visibleParagraphs={visibleParagraphs}
      transcriptID={transcriptID}
      select={hasEditPermission ? select : null}
      selectedExcerptId={hasEditPermission ? selectedExcerptId : null}
      canEdit={false}
      isPrint={true}
      enableHover
      hasEditPermission={hasEditPermission}
      showCodeSelector={showCodeSelector}
      marginLeft={marginLeft}
      marginRight={marginRight}
      leftUserID={filterUserID}
    />
  );
};

/**
 * Renders a single column of the comparison view. If the column shows the current user,
 * wraps with editing providers so codes can be added and removed. The other user's
 * column is "remove-only": codes can still be removed (when the viewer has edit
 * permission) but the add-code selector is hidden.
 * In grid mode (side-by-side), renders ComparisonSideGrid cells with explicit gridRow.
 * In non-grid mode (solo), renders ComparisonSide as a full column.
 */
const ComparisonColumn = ({ isCurrentUser, projectID, transcriptID, hasEditPermission, sharedProps, filterUserID, gridColumn, isGrid, style, setParagraphRef, extraBoundariesByParagraph, onSelectionChange }) => {
  const SideComponent = isGrid ? ComparisonSideGrid : ComparisonSide;
  const sideProps = {
    ...sharedProps,
    hasEditPermission,                                       // both sides can remove codes
    showCodeSelector: isCurrentUser ? hasEditPermission : false, // only own side can add codes
    filterUserID,
    ...(isGrid ? { gridColumn, setParagraphRef, extraBoundariesByParagraph, onSelectionChange } : {}),
  };

  if (hasEditPermission) {
    return (
      <EditableSideWrapper projectId={projectID} transcriptID={transcriptID} hasEditPermission={hasEditPermission} displayContents={isGrid} codingUserId={filterUserID}>
        <SideComponent {...sideProps} />
      </EditableSideWrapper>
    );
  }
  return <SideComponent {...sideProps} />;
};

const ComparisonMainContent = ({
  transcriptID,
  projectID,
  leftUserID,
  rightUserID,
  page,
  pageCount,
  onPage,
  users,
  onLeftUserSelect,
  onRightUserSelect,
  selectedUsers,
  visibleParagraphs,
  isSideBySide,
  hasEditPermission,
  diffsByParagraph,
  extraBoundariesByParagraph,
  paragraphRefs,
  setParagraphRef,
  scrollbarsRef,
  onSelectionChange
}) => {
  const { data: currentUser } = useCurrentUser();
  const currentUserId = currentUser?.id;
  const isCurrentUser = (userID) => Boolean(currentUserId) && String(userID) === String(currentUserId);
  const leftIsCurrentUser = isCurrentUser(leftUserID);
  const rightIsCurrentUser = isCurrentUser(rightUserID);

  const sharedProps = {
    visibleParagraphs,
    transcriptID,
    canEdit: false,
    isPrint: true,
    marginLeft: PADDING,
    marginRight: "20px",
    layout: 'single', // Each side renders as single column
    currentUserId
  };

  if (isSideBySide) {
    return (
      <Scrollbars autoHide style={{ height: "100%" }} ref={scrollbarsRef}>
        {/* Small constant sheet; the stamping effect drives which elements
            it colors via data attributes. Never re-parsed. */}
        <style>{AGREEMENT_STATIC_CSS}</style>
        <PagerWrapper pageCount={pageCount} onPage={onPage}>
          <UserDropdownHeader
            users={users}
            selectedUsers={selectedUsers}
            onLeftUserSelect={onLeftUserSelect}
            onRightUserSelect={onRightUserSelect}
            layout="sideBySide"
          />
          <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: '1fr 28px 1fr' }}>
            <ComparisonColumn isCurrentUser={leftIsCurrentUser} projectID={projectID} transcriptID={transcriptID} hasEditPermission={hasEditPermission} sharedProps={sharedProps} filterUserID={leftUserID} gridColumn={1} isGrid setParagraphRef={setParagraphRef} extraBoundariesByParagraph={extraBoundariesByParagraph} onSelectionChange={onSelectionChange} />
            {visibleParagraphs.length > 0 && (
              // The gutter cell must contribute ZERO height to the grid's
              // auto rows: the strip inside is a sticky child roughly one
              // viewport tall, and if it sized the cell, short pages would
              // stretch their paragraph rows apart to fit it. The absolute
              // rail spans the grid's full height (set by the text columns
              // alone) and the sticky strip travels inside it.
              <div style={{ gridColumn: 2, gridRow: `1 / ${visibleParagraphs.length + 1}`, position: 'relative' }}>
                <div style={{ position: 'absolute', inset: 0 }}>
                  <ComparisonHeatmapStrip
                    paragraphs={visibleParagraphs}
                    diffsByParagraph={diffsByParagraph}
                    paragraphRefs={paragraphRefs}
                    scrollbarsRef={scrollbarsRef}
                    // Single page: nothing renders below the grid, so the
                    // strip may run the full pane height on a short
                    // transcript (see fillViewport in the strip).
                    fillViewport={(pageCount || 1) <= 1}
                  />
                </div>
              </div>
            )}
            <ComparisonColumn isCurrentUser={rightIsCurrentUser} projectID={projectID} transcriptID={transcriptID} hasEditPermission={hasEditPermission} sharedProps={sharedProps} filterUserID={rightUserID} gridColumn={3} isGrid extraBoundariesByParagraph={extraBoundariesByParagraph} onSelectionChange={onSelectionChange} />
          </div>
        </PagerWrapper>
      </Scrollbars>
    );
  }

  // Solo mode
  return (
    <Scrollbars autoHide style={{ height: "100%", flex: 1 }}>
      <PagerWrapper pageCount={pageCount} onPage={onPage}>
        <UserDropdownHeader
          users={users}
          selectedUsers={selectedUsers}
          onLeftUserSelect={onLeftUserSelect}
          onRightUserSelect={onRightUserSelect}
          layout="single"
        />
        <div style={{ marginTop: '10px' }}>
          <ComparisonColumn isCurrentUser={leftIsCurrentUser} projectID={projectID} transcriptID={transcriptID} hasEditPermission={hasEditPermission} sharedProps={sharedProps} filterUserID={leftUserID} />
        </div>
      </PagerWrapper>
    </Scrollbars>
  );
};

const ComparisonView = ({
  transcriptID,
  projectID,
  transcriptData,
  excerpts = EMPTY_ARRAY,
  leftUserID,
  rightUserID,
  page,
  pageCount,
  onPage,
  users,
  onLeftUserSelect,
  onRightUserSelect,
  selectedUsers,
  hasEditPermission
}) => {
  const isSideBySide = Boolean(rightUserID);
  const { visibleParagraphs } = useTranscriptData(transcriptData?.body, page);

  // The native chip-hover cross-highlight: hovering a code chip sets the
  // shared ActiveCodeContext, which both columns' paragraphs read — spans
  // coded with that code get a single underline in the code's own color
  // (SUPERCODED), every other coded span drops its underline, and the other
  // chips grey out. All of that is painted through inline styles, which the
  // agreement overlay's !important rules would silently beat. So we track
  // the active code here too.
  const { activeCodeId } = useActiveCode();

  // The transcript's excerpts, resolved through the detail caches by the
  // page's single useTranscriptExcerpts instance in
  // ComparisonViewContainer and passed down (ids-only model — useExcerpt's
  // entries are the single store). Any coding write re-renders with fresh
  // data, so the overlay recolors live with edits on either side.
  // leftUserID/rightUserID arrive as raw query-string values, while
  // excerpt.codings[].user_id is numeric — run them through the same
  // parseUserId ParagraphContainer uses before matching.
  const parsedLeftUserID = ComparisonUrlUtil.parseUserId(leftUserID);
  const parsedRightUserID = ComparisonUrlUtil.parseUserId(rightUserID);
  const leftExcerpts = useMemo(() => filterCodingsToUser(excerpts, parsedLeftUserID), [excerpts, parsedLeftUserID]);
  const rightExcerpts = useMemo(() => filterCodingsToUser(excerpts, parsedRightUserID), [excerpts, parsedRightUserID]);

  // The live text selection per column ({ start, end } absolute offsets, or
  // null), reported by each ComparisonSideGrid — feeds extra cut points into
  // the recolor CSS below so re-cut spans keep their styling. Keyed by grid
  // column (1 left, 3 right); only the column whose CodeableContext holds
  // the selection re-cuts.
  const [columnSelections, setColumnSelections] = useState({});
  const handleSelectionChange = useCallback((gridColumn, selectionData) => {
    setColumnSelections((previous) => {
      const next = selectionData
        ? { start: selectionData.selectionStart, end: selectionData.selectionEnd }
        : null;
      const current = previous[gridColumn] || null;
      if (current === next || (current && next && current.start === next.start && current.end === next.end)) {
        return previous;
      }
      return { ...previous, [gridColumn]: next };
    });
  }, []);

  // The whole per-paragraph overlay model (diff, underline spans, chip
  // verdicts, tooltip segments, extraBoundaries), computed through the
  // signature cache: an excerpt edit recomputes only the paragraphs it
  // touched, and untouched paragraphs keep their entry IDENTITIES — which is
  // what stops one edit from re-rendering every memoized ParagraphContainer
  // (their extraBoundaries prop) and rebuilding every paragraph's sentences.
  const overlayCacheRef = useRef(createOverlayCache());
  // Entries are keyed by paragraph start:end with no transcript identity,
  // and this component can survive navigation between transcripts' compare
  // pages (cached transcripts skip the loading unmount) — reset the cache
  // so one transcript's entries are never served for another's paragraphs.
  const overlayTranscriptRef = useRef(transcriptID);
  if (overlayTranscriptRef.current !== transcriptID) {
    overlayTranscriptRef.current = transcriptID;
    overlayCacheRef.current = createOverlayCache();
  }
  const overlayByParagraph = useMemo(() => {
    if (!isSideBySide) return EMPTY_ARRAY;
    return buildOverlayEntries(
      overlayCacheRef.current,
      visibleParagraphs,
      leftExcerpts,
      rightExcerpts,
      columnSelections
    );
  }, [isSideBySide, visibleParagraphs, leftExcerpts, rightExcerpts, columnSelections]);

  const diffsByParagraph = useMemo(
    () => overlayByParagraph.map((entry) => entry.diff),
    [overlayByParagraph]
  );

  // Code names for the hover tooltip's per-coder lists — the only consumer
  // of names in this diff pipeline (everything else works on ids and
  // colors). Served from the react-query cache the rest of the app already
  // keeps warm.
  const { data: codesData } = useCodes(projectID);
  const codeNamesById = useMemo(() => {
    const map = {};
    (codesData?.codes || []).forEach((code) => { map[code.id] = code.name; });
    return map;
  }, [codesData]);

  // Flat lookup list for the hover handler: every visible segment that gets
  // a tooltip, in one array — absolute offsets are unique across paragraphs,
  // so a plain range scan resolves a hovered span without knowing which
  // paragraph it belongs to.
  const tooltipSegments = useMemo(
    () => overlayByParagraph.flatMap((entry) => entry.tooltipSegments),
    [overlayByParagraph]
  );

  // Which tooltip segment (if any) the mouse is over, plus where to anchor
  // the card. A delegated mousemove listener rather than per-span React
  // handlers, because the span DOM is owned by the shared SentenceComponent/
  // SubElementComponent chain — this page can't attach handlers there
  // without touching every transcript view. (A native `title` attribute was
  // rejected in the proto: ~1s hover delay, tiny, easy to miss.)
  //
  // The card shows after a short delay so it doesn't strobe while the mouse
  // merely crosses the text (per Alex) — but once a card is up, moving onto
  // another explainable span re-anchors it instantly, the same warm-vs-cold
  // rhythm native OS tooltips have, so deliberate span-to-span reading
  // doesn't stutter. Any hide (plain text, scroll, leaving the window)
  // resets to cold.
  const [hoverTooltip, setHoverTooltip] = useState(null);
  useEffect(() => {
    // Segments recomputed (a mutation, or the other coder's cable edit):
    // any card on screen describes the OLD data and is anchored to a stale
    // rect — drop it rather than keep asserting a verdict that may no
    // longer exist; the next mousemove re-resolves against the new
    // segments (cold, per the warm/cold rhythm above).
    setHoverTooltip(null);
    if (!isSideBySide || !tooltipSegments.length) {
      return undefined;
    }
    let currentEl = null;
    let showTimer = null;
    let visible = false;
    const show = (payload) => {
      visible = true;
      setHoverTooltip(payload);
    };
    const hide = () => {
      clearTimeout(showTimer);
      visible = false;
      setHoverTooltip(null);
    };
    const onMove = (e) => {
      if (!e.target.closest) return;
      const span = e.target.closest('.codeSpan[start]');
      // Only spans inside a comparison row. Must be ONE .closest() call
      // requiring both attributes on the same element: ParagraphLayout sets
      // its own data-paragraph-index (from a different index) deeper in the
      // tree, so matching on the pair is what finds the real row div.
      const row = span && span.closest('[data-paragraph-index][data-grid-column]');
      const target = row ? span : null;
      if (target === currentEl) return;
      currentEl = target;
      clearTimeout(showTimer);
      const offset = target ? parseInt(target.getAttribute('start'), 10) : NaN;
      const segment = target ? tooltipSegments.find((s) => offset >= s.start && offset < s.end) : null;
      // The renderer draws a .codeSpan for uncoded text too, and a one-sided
      // segment covers text that is coded in only ONE column — require the
      // hovered span's own column to have codes there, so plain text in the
      // other column doesn't sprout a tooltip.
      const ownCodeIds = segment &&
        (row.getAttribute('data-grid-column') === '1' ? segment.leftCodeIds : segment.rightCodeIds);
      if (!segment || !ownCodeIds.length) {
        hide();
        return;
      }
      const rect = target.getBoundingClientRect();
      const payload = { segment, top: rect.top, bottom: rect.bottom, left: rect.left };
      if (visible) show(payload);
      else showTimer = setTimeout(() => show(payload), TOOLTIP_SHOW_DELAY_MS);
    };
    const onLeave = () => {
      currentEl = null;
      hide();
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseleave', onLeave);
    // The card anchors to a rect captured at hover time; scrolling moves the
    // text out from under it, so hide until the next mousemove re-resolves.
    document.addEventListener('scroll', onLeave, true);
    return () => {
      clearTimeout(showTimer);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
      document.removeEventListener('scroll', onLeave, true);
    };
  }, [isSideBySide, tooltipSegments]);

  // Per paragraph, the diff-segment edges as ABSOLUTE character offsets — the
  // union of both coders' excerpt boundaries. Both columns' paragraph
  // renderers split their spans at these in addition to their own excerpt
  // boundaries (SentenceBuilder's extraBoundaries), so every rendered span
  // falls entirely inside one diff segment and the underline recolor below is
  // exact — including a one-sided slice in the middle of one coder's long
  // excerpt. Side-by-side only: solo mode draws no agreement overlay, so the
  // extra splits would be pointless DOM churn.
  const extraBoundariesByParagraph = useMemo(() => {
    if (!isSideBySide) return null;
    // The INNER arrays come from the overlay cache, so an unchanged
    // paragraph passes the identical extraBoundaries identity into its
    // memoized ParagraphContainer — no re-render, no sentence rebuild.
    return overlayByParagraph.map((entry) => entry.extraBoundaries);
  }, [isSideBySide, overlayByParagraph]);

  // Registered by the left column's ComparisonSideGrid rows so the heatmap
  // strip can find each paragraph's real on-screen position. Both columns
  // are row-aligned in the grid (same gridRow per paragraph index), so one
  // side's refs are enough.
  const paragraphRefs = useRef({});
  const setParagraphRef = (index, el) => {
    paragraphRefs.current[index] = el;
  };

  // The page's scroll container (the Scrollbars instance wrapping this
  // view), measured by the heatmap strip to map document positions onto the
  // minimap and drive its viewport window.
  const scrollbarsRef = useRef(null);

  // The hover skin's span classifications: while a chip is hovered, the
  // native cross-highlight hides every other code's spans and greys every
  // other chip (inline styles, untouched by us) — but the hovered code's
  // OWN marks keep agreement colors RECOMPUTED for just that code
  // (buildHoverUnderlineSpans): isolating one code dissolves 'partial' into
  // green (the other coder applied this code here too), red (they coded
  // this text, but never with this code), or gray (they left it uncoded)
  // stretch by stretch.
  const hoverSpansByParagraph = useMemo(() => {
    if (!isSideBySide || activeCodeId == null) return null;
    return visibleParagraphs.map((paragraph, i) => {
      const entry = overlayByParagraph[i];
      if (!entry) return null;
      const toMap = (spans) => new Map(spans.map((span) => [String(span.start), span.classification]));
      return {
        1: toMap(buildHoverUnderlineSpans(paragraph, leftExcerpts, rightExcerpts, activeCodeId, entry.diff, selectionCutsForParagraph(paragraph, columnSelections[1]))),
        3: toMap(buildHoverUnderlineSpans(paragraph, rightExcerpts, leftExcerpts, activeCodeId, entry.diff, selectionCutsForParagraph(paragraph, columnSelections[3]))),
      };
    });
  }, [isSideBySide, activeCodeId, visibleParagraphs, overlayByParagraph, leftExcerpts, rightExcerpts, columnSelections]);

  // Stamps each rendered span's agreement classification and each chip's
  // verdict onto the DOM as data attributes, which the small STATIC
  // stylesheet (AGREEMENT_STATIC_CSS) colors — see its comment for why this
  // replaced generated per-span rules. The DOM contract is unchanged: every
  // rendered .codeSpan carries its absolute offset in a `start` attribute
  // and splits at the column's own excerpt boundaries PLUS the diff-segment
  // edges (extraBoundariesByParagraph) plus any live selection's boundaries,
  // so each span lies inside exactly one segment and entry.underlineSpans
  // has exactly one classification for it. Stamps are per column — the same
  // excerpt record can carry BOTH coders' codings.
  //
  // React leaves these unmanaged attributes alone on updates; when a
  // paragraph's DOM is actually rebuilt, the same data change bumps its
  // overlay entry, so this effect re-runs. A layout effect, so stamps land
  // before paint. While a chip is hovered, spans re-stamp from the hover
  // maps (spans not covered fall back to the native hiding) and only the
  // active code's chips keep a verdict — the rest grey out natively.
  useLayoutEffect(() => {
    if (!isSideBySide) return;
    const entryByAbsoluteIndex = new Map();
    visibleParagraphs.forEach((paragraph, i) => {
      entryByAbsoluteIndex.set(String(paragraph.paragraphIndex), i);
    });
    document.querySelectorAll('[data-grid-column][data-paragraph-index]').forEach((row) => {
      const i = entryByAbsoluteIndex.get(row.getAttribute('data-paragraph-index'));
      const entry = i === undefined ? null : overlayByParagraph[i];
      if (!entry) return;
      const column = Number(row.getAttribute('data-grid-column'));

      const spanMap = hoverSpansByParagraph
        ? (hoverSpansByParagraph[i] || {})[column] || new Map()
        : new Map((column === 1 ? entry.underlineSpans1 : entry.underlineSpans3)
            .map((span) => [String(span.start), span.classification]));
      Array.prototype.forEach.call(row.getElementsByClassName('codeSpan'), (span) => {
        const classification = spanMap.get(span.getAttribute('start'));
        if (classification) span.setAttribute('data-agreement', classification);
        else span.removeAttribute('data-agreement');
      });

      const verdictMap = new Map();
      entry.verdicts.forEach(({ gridColumn, codeId, verdict }) => {
        if (gridColumn === column) verdictMap.set(String(codeId), verdict);
      });
      entry.oneSidedChipCodes.forEach(({ gridColumn, codeId }) => {
        if (gridColumn === column) verdictMap.set(String(codeId), 'oneSided');
      });
      row.querySelectorAll('[data-code-id]').forEach((chip) => {
        const codeId = chip.getAttribute('data-code-id');
        const verdict =
          activeCodeId != null && Number(codeId) !== Number(activeCodeId)
            ? null
            : verdictMap.get(codeId);
        if (verdict) chip.setAttribute('data-verdict', verdict);
        else chip.removeAttribute('data-verdict');
      });
    });
    // codesData is a dep because chips mount when the codes list resolves:
    // a stamp pass that ran before ParagraphCodeList rendered its chips
    // left them unstamped (white) with nothing to re-trigger — under the
    // ids-only resolution the overlay often settles BEFORE the codes
    // fetch, which made this pre-existing race frequent (caught by the
    // agreement Cypress suite flaking on chip colors).
  }, [isSideBySide, visibleParagraphs, overlayByParagraph, hoverSpansByParagraph, activeCodeId, codesData]);

  if (!transcriptData?.body) {
    return <PageLoadingComponent />;
  }

  const mainContentProps = {
    transcriptID,
    projectID,
    leftUserID,
    rightUserID,
    page,
    pageCount,
    onPage,
    users,
    onLeftUserSelect,
    onRightUserSelect,
    selectedUsers,
    visibleParagraphs,
    isSideBySide,
    hasEditPermission,
    diffsByParagraph,
    extraBoundariesByParagraph,
    paragraphRefs,
    setParagraphRef,
    scrollbarsRef,
    onSelectionChange: handleSelectionChange
  };

  if (!isSideBySide) {
    return (
      <div style={{ display: 'flex', height: "100%" }}>
        <ComparisonMainContent {...mainContentProps} />
        <div style={{ flex: 1 }}>
          <ShareToCompare />
        </div>
      </div>
    );
  }

  return (
    <>
      <ComparisonMainContent {...mainContentProps} />
      {hoverTooltip && (
        <ComparisonAgreementTooltip
          tooltip={hoverTooltip}
          content={tooltipContentFor(
            hoverTooltip.segment,
            codeNamesById,
            displayName(selectedUsers?.leftUser),
            displayName(selectedUsers?.rightUser)
          )}
        />
      )}
    </>
  );
};

export default ComparisonView;
